import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { FilterMetadata } from 'primeng/api';
import { map, Observable, throwError } from 'rxjs';
import { retry, catchError } from 'rxjs/operators';

import { GetglobalsettingsService } from '../services/getglobalsettings.service'
import { DocListEntry } from '../shared/doc-list-entry';
import { DocItemStockSummary } from '../shared/doc-item-stock-summary';
import { Document, DocItems, DocumentFastSearchResult } from 'src/app/shared/document';
import { DocTypes } from '../shared/doc-list-entry';
import { DoclistQueryParams } from '../shared/doclist-query-params';
import { DocTreeNode } from '../shared/doc-tree-node';
import { EnrichedDocItem } from '../shared/enriched-doc-item';
import { ManufacturingBackdocRecompute, ManufacturingBackdocRecomputeBreakdownRow } from '../shared/manufacturing-backdoc-recompute';

/**
 * REST client for all document lifecycle operations, covering:
 * - Document header CRUD (`/Document`)
 * - Document list queries (`/DocumentList`)
 * - Document line-item CRUD (`/DocItems`, `/DocItemsList`)
 * - Document type reference data (`/DocTypes`)
 *
 * Every request includes the standard Webyto headers (DB server, DB name,
 * DB user, access group, main company, timezone offset, content-type) via
 * {@link GetglobalsettingsService.getStandardApiHeaders}, plus this service's
 * own `Webyto-Main-Currency` header. Headers are resolved fresh per call so
 * Setup-screen overrides take effect without a page reload.
 *
 * All methods apply `retry(1)` on network failure. Errors are surfaced via
 * {@link handleError}, which shows a browser `alert` (legacy pattern flagged
 * for Phase 1 replacement).
 */
@Injectable({
  providedIn: 'root'
})
export class DocumentApiService {

  apiURL: string = '';

  constructor(
    private http: HttpClient,
    private globalsettingsservice: GetglobalsettingsService,
  ) {
  }

  /**
   * Builds an absolute API URL by prepending the runtime API server address.
   *
   * @param url - Path starting with `/` (e.g. `/DocumentList/123`).
   * @returns Absolute URL to the backend endpoint.
   */
  private _getApiUrl(url: string = ''): string {
    let _api: string = this.globalsettingsservice.getApiServerUrl();
    return _api + url;
  }

  /**
   * Revives `DocListEntry` date fields (raw strings over the wire, despite the
   * `Date` type annotation — JSON has no date type and `HttpClient` never
   * parses them back) into real `Date` objects, in place. Mirrors the
   * boundary revival {@link getDocumentById} does for `Document`.
   */
  private _reviveDocListEntryDates(d: DocListEntry): DocListEntry {
    if (d.DOCDATE) { d.DOCDATE = new Date(d.DOCDATE); }
    if (d.DOCEXPDATE) { d.DOCEXPDATE = new Date(d.DOCEXPDATE); }
    if (d.DOCVALDATE) { d.DOCVALDATE = new Date(d.DOCVALDATE); }
    if (d.PAIDNOWDATE) { d.PAIDNOWDATE = new Date(d.PAIDNOWDATE); }
    return d;
  }

  /** Array form of {@link _reviveDocListEntryDates}, applied in place to every row. */
  private _reviveDocListEntryArrayDates(data: DocListEntry[]): DocListEntry[] {
    data.forEach(d => this._reviveDocListEntryDates(d));
    return data;
  }

  // ---------------------------------------------------------------------------
  // Document list queries
  // ---------------------------------------------------------------------------

  /**
   * Returns the document list for a given company/view combination.
   *
   * Calls `GET /DocumentList/:id?_listType=<viewid>`.
   *
   * @param id - Company or context ID scoping the list.
   * @param viewid - List-view type ID controlling which TVF variant is used.
   * @returns Observable emitting an array of `DocListEntry` records.
   */
  getDocList(id: number, viewid: number): Observable<DocListEntry[]> {
    return this.http.get<DocListEntry[]>(this._getApiUrl('/DocumentList/' + id + '?_listType=' + viewid), {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Main-Currency': this.globalsettingsservice.getMainCurrencyId().toString(),
        }
      })
      .pipe(
        retry(1),
        map(data => this._reviveDocListEntryArrayDates(data)),
        catchError(this.handleError)
      )
  }

  /**
   * Returns direct child documents of a given parent document.
   *
   * Calls `GET /DocumentList/child/:id`.
   *
   * @deprecated For full tree rendering use {@link getDocTree} instead. This
   *   method is retained for callers that need only one level of children.
   *
   * @param id - Parent document ID (`DOCID`).
   * @returns Observable emitting direct child `DocListEntry` records.
   */
  getChildDocList(id: number): Observable<DocListEntry[]> {
    return this.http.get<DocListEntry[]>(this._getApiUrl('/DocumentList/child/' + id), {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Main-Currency': this.globalsettingsservice.getMainCurrencyId().toString(),
        }
      })
      .pipe(
        retry(1),
        map(data => this._reviveDocListEntryArrayDates(data)),
        catchError(this.handleError)
      )
  }

  /**
   * Returns the immediate parent document entry for the given document ID.
   *
   * Calls `GET /DocumentList/parent/:id`.
   *
   * @param id - Child document ID (`DOCID`).
   * @returns Observable emitting a one-element array with the parent
   *   `DocListEntry`, or an empty array if the document has no parent.
   */
  getParentDocListEntry(id: number): Observable<DocListEntry[]> {
    return this.http.get<DocListEntry[]>(this._getApiUrl('/DocumentList/parent/' + id), {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Main-Currency': this.globalsettingsservice.getMainCurrencyId().toString(),
        }
      })
      .pipe(
        retry(1),
        map(data => this._reviveDocListEntryArrayDates(data)),
        catchError(this.handleError)
      )
  }

  /**
   * Returns the topmost ancestor document in the hierarchy.
   *
   * Calls `GET /DocumentList/topdoc/:id`.
   *
   * @deprecated For full tree rendering use {@link getDocTree} instead. This
   *   method is retained for callers that need only the root document.
   *
   * @param id - Any document ID (`DOCID`) in the hierarchy.
   * @returns Observable emitting the root `DocListEntry`.
   */
  getTopDocListEntry(id: number): Observable<DocListEntry> {
    return this.http.get<DocListEntry>(this._getApiUrl('/DocumentList/topdoc/' + id), {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Main-Currency': this.globalsettingsservice.getMainCurrencyId().toString(),
        }
      })
      .pipe(
        retry(1),
        map(data => this._reviveDocListEntryDates(data)),
        catchError(this.handleError)
      )
  }

  /**
   * Returns the complete document hierarchy for any node in the tree as a flat,
   * path-ordered array.
   *
   * Calls `GET /DocumentList/tree/:docId`. Backed by `dbo.udf_doctree_iTVF` —
   * single SQL call with no N+1. Works from any node: leaf, root, or middle.
   *
   * @param docId - Any `DOCID` in the target tree.
   * @returns Observable emitting `DocTreeNode[]` ordered by hierarchy path.
   */
  getDocTree(docId: number): Observable<DocTreeNode[]> {
    return this.http.get<DocTreeNode[]>(
      this._getApiUrl('/DocumentList/tree/' + docId),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Main-Currency': this.globalsettingsservice.getMainCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Returns the `DocListEntry` record for a single document by ID.
   *
   * Calls `GET /DocumentList/document/:id`.
   *
   * @param id - Document ID (`DOCID`).
   * @returns Observable emitting a one-element `DocListEntry` array.
   */
  getDocListEntry(id: number): Observable<DocListEntry[]> {
    return this.http.get<DocListEntry[]>(this._getApiUrl('/DocumentList/document/' + id), {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Main-Currency': this.globalsettingsservice.getMainCurrencyId().toString(),
        }
      })
      .pipe(
        retry(1),
        map(data => this._reviveDocListEntryArrayDates(data)),
        catchError(this.handleError)
      )
  }

  /**
   * Returns the document list for a date range with amounts converted to a
   * specified currency.
   *
   * Calls `GET /DocumentList/withcurrency/?_listType=...`.
   *
   * @param viewid - List-view type ID.
   * @param fromDate - Start of the date range.
   * @param toDate - End of the date range.
   * @param currency - Currency ID for amount conversion. Defaults to `102` (UAH).
   * @returns Observable emitting `DocListEntry[]` with currency-converted totals.
   */
  getDocListBetweenDatesWithCurrency(viewid: number, fromDate: Date, toDate: Date, currency: number = 102): Observable<DocListEntry[]> {
    let parmStr: string = '?_listType=' + viewid + '&_currency=' + currency + '&_fromDate=' + (fromDate ? new Date(fromDate) : new Date()).toISOString() + '&_toDate=' + (toDate ? new Date(toDate) : new Date()).toISOString();
    return this.http.get<DocListEntry[]>(this._getApiUrl('/DocumentList/withcurrency/' + parmStr), {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Main-Currency': this.globalsettingsservice.getMainCurrencyId().toString(),
        }
      })
      .pipe(
//        retry(1),
        map(data => this._reviveDocListEntryArrayDates(data)),
        catchError(this.handleError)
      )
  }

  /**
   * Returns the document list filtered to a date range.
   *
   * Calls `GET /DocumentList/?_listType=...`. Differs from
   * {@link getDocListBetweenDatesWithCurrency} in that it uses the plain
   * `/DocumentList/` path without the currency conversion variant.
   *
   * @param viewid - List-view type ID.
   * @param fromDate - Start of the date range.
   * @param toDate - End of the date range.
   * @param currency - Currency ID (passed to the TVF but amounts may not be
   *   converted in all list views). Defaults to `102` (UAH).
   * @returns Observable emitting `DocListEntry[]`.
   */
  getDocListBetweenDates(viewid: number, fromDate: Date, toDate: Date, currency: number = 102): Observable<DocListEntry[]> {
    let parmStr: string = '?_listType=' + viewid + '&_currency=' + currency + '&_fromDate=' + (fromDate ? new Date(fromDate) : new Date()).toISOString() + '&_toDate=' + (toDate ? new Date(toDate) : new Date()).toISOString();
    return this.http.get<DocListEntry[]>(this._getApiUrl('/DocumentList/' + parmStr), {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Main-Currency': this.globalsettingsservice.getMainCurrencyId().toString(),
        }
      })
      .pipe(
//        retry(1),
        map(data => this._reviveDocListEntryArrayDates(data)),
        catchError(this.handleError)
      )
  }

  /**
   * Returns documents associated with a specific client, filtered by date
   * and up to four free-text search strings.
   *
   * Calls `GET /DocumentList/ByClient/:id?...`.
   *
   * @param id - Client ID (`CLIID`) to filter by.
   * @param viewid - List-view type ID.
   * @param fromDate - Reference start date for the query.
   * @param str1 - First search string filter.
   * @param str2 - Second search string filter.
   * @param str3 - Third search string filter.
   * @param str4 - Fourth search string filter.
   * @returns Observable emitting matching `DocListEntry[]` records.
   */
  getDocListDateByClient(id: number, viewid: number, fromDate: Date, str1: string, str2: string, str3: string, str4: string): Observable<DocListEntry[]> {
    let parmStr: string = '?_listType=' + viewid + '&_fromDate=' + (fromDate ? new Date(fromDate) : new Date()).toISOString() + '&_sstr1=' + encodeURIComponent(str1) + '&_sstr2=' + encodeURIComponent(str2) + '&_sstr3=' + encodeURIComponent(str3) + '&_sstr4=' + encodeURIComponent(str4);
    return this.http.get<DocListEntry[]>(this._getApiUrl('/DocumentList/ByClient/' + id + parmStr), {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Main-Currency': this.globalsettingsservice.getMainCurrencyId().toString(),
        }
      })
      .pipe(
//        retry(1),
        map(data => this._reviveDocListEntryArrayDates(data)),
        catchError(this.handleError)
      )
  }

  /**
   * Returns documents that contain a specific product, filtered by date
   * and up to four free-text search strings.
   *
   * Calls `GET /DocumentList/ByProduct/:id?...`.
   *
   * @param id - Product ID (`PRODID`) to filter by.
   * @param viewid - List-view type ID.
   * @param fromDate - Reference start date for the query.
   * @param str1 - First search string filter.
   * @param str2 - Second search string filter.
   * @param str3 - Third search string filter.
   * @param str4 - Fourth search string filter.
   * @returns Observable emitting matching `DocListEntry[]` records.
   */
  getDocListDateByProduct(id: number, viewid: number, fromDate: Date, str1: string, str2: string, str3: string, str4: string): Observable<DocListEntry[]> {
    let parmStr: string = '?_listType=' + viewid + '&_fromDate=' + (fromDate ? new Date(fromDate) : new Date()).toISOString() + '&_sstr1=' + encodeURIComponent(str1) + '&_sstr2=' + encodeURIComponent(str2) + '&_sstr3=' + encodeURIComponent(str3) + '&_sstr4=' + encodeURIComponent(str4);
    return this.http.get<DocListEntry[]>(this._getApiUrl('/DocumentList/ByProduct/' + id + parmStr), {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Main-Currency': this.globalsettingsservice.getMainCurrencyId().toString(),
        }
      })
      .pipe(
//        retry(1),
        map(data => this._reviveDocListEntryArrayDates(data)),
        catchError(this.handleError)
      )
  }

  /**
   * Returns a document list scoped by company/context, date, and four search
   * strings. Computes `PARTNERNAMESTR` on each record: shows the full partner
   * name alone, or `alias (full name)` when the client differs from the
   * purchasing company.
   *
   * Calls `GET /DocumentList/:id?_listType=...`.
   *
   * @param id - Company or context ID.
   * @param viewid - List-view type ID.
   * @param fromDate - Reference start date.
   * @param str1 - First search string filter.
   * @param str2 - Second search string filter.
   * @param str3 - Third search string filter.
   * @param str4 - Fourth search string filter.
   * @returns Observable emitting `DocListEntry[]` with `PARTNERNAMESTR` set.
   */
  getDocListDateEx(id: number, viewid: number, fromDate: Date, str1: string, str2: string, str3: string, str4: string): Observable<DocListEntry[]> {
    let parmStr: string = '?_listType=' + viewid + '&_fromDate=' + (fromDate ? new Date(fromDate) : new Date()).toISOString() + '&_sstr1=' + encodeURIComponent(str1) + '&_sstr2=' + encodeURIComponent(str2) + '&_sstr3=' + encodeURIComponent(str3) + '&_sstr4=' + encodeURIComponent(str4);
    return this.http.get<DocListEntry[]>(this._getApiUrl('/DocumentList/' + id + parmStr), {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Main-Currency': this.globalsettingsservice.getMainCurrencyId().toString(),
        }
      })
      .pipe(
        retry(1),
        map((data: DocListEntry[]) => {
          data.forEach(dc => dc.PARTNERNAMESTR = (dc.DOCCLIENTSID == dc.DOCPCOCLIID) ? dc.PARTNERNAME : dc.PARTNERNIKNAME + ' (' + dc.PARTNERNAME + ')');
          return this._reviveDocListEntryArrayDates(data);
        }),
        catchError(this.handleError),
      )
  }

  /**
   * Returns a document list scoped by company/context and a single date filter.
   *
   * Calls `GET /DocumentList/:id?_listType=<viewid>&_fromDate=<fromDate>`.
   *
   * @param id - Company or context ID.
   * @param viewid - List-view type ID.
   * @param fromDate - Reference start date.
   * @returns Observable emitting `DocListEntry[]`.
   */
  getDocListDate(id: number, viewid: number, fromDate: Date): Observable<DocListEntry[]> {
    return this.http.get<DocListEntry[]>(this._getApiUrl('/DocumentList/' + id + '?_listType=' + viewid + '&_fromDate=' + (fromDate ? new Date(fromDate) : new Date()).toISOString()), {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Main-Currency': this.globalsettingsservice.getMainCurrencyId().toString(),
        }
      })
      .pipe(
        retry(1),
        map(data => this._reviveDocListEntryArrayDates(data)),
        catchError(this.handleError)
      )
  }

  /**
   * Returns a paginated slice of the document list.
   *
   * Calls `GET /DocumentList/:id?_start=<startRow>&_end=<endRow>&_listType=<viewid>`.
   *
   * @param id - Company or context ID.
   * @param viewid - List-view type ID.
   * @param startRow - First row index (zero-based).
   * @param endRow - Last row index (exclusive).
   * @returns Observable emitting the requested `DocListEntry[]` slice.
   */
  getDocListSlice(id: number, viewid: number, startRow: number, endRow: number): Observable<DocListEntry[]> {
    return this.http.get<DocListEntry[]>(this._getApiUrl('/DocumentList/' + id + '?_start=' + startRow + '&_end=' + endRow + '&_listType=' + viewid), {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Main-Currency': this.globalsettingsservice.getMainCurrencyId().toString(),
        }
      })
      .pipe(
        retry(1),
        map(data => this._reviveDocListEntryArrayDates(data)),
        catchError(this.handleError)
      )
  }

  // ---------------------------------------------------------------------------
  // Document type reference data
  // ---------------------------------------------------------------------------

  /**
   * Returns all document type records from the `DOCTYPES` reference table.
   *
   * Calls `GET /DocTypes`.
   *
   * @returns Observable emitting an array of `DocTypes` records.
   */
  getDocTypes(): Observable<DocTypes[]> {
    return this.http.get<DocTypes[]>(this._getApiUrl('/DocTypes'), {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Main-Currency': this.globalsettingsservice.getMainCurrencyId().toString(),
        }
      })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Returns a single document type record by ID.
   *
   * Calls `GET /DocTypes/:id`.
   *
   * @param id - `DOCTYPEID` primary key.
   * @returns Observable emitting a one-element `DocTypes` array.
   */
  getDocTypeById( id: number): Observable<DocTypes[]> {
    return this.http.get<DocTypes[]>(this._getApiUrl('/DocTypes/' + id), {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Main-Currency': this.globalsettingsservice.getMainCurrencyId().toString(),
        }
      })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Updates a document type record.
   *
   * Calls `PUT /DocTypes/:DOCTYPEID`.
   *
   * @param doctype - The `DocTypes` object to update. `DOCTYPEID` must be set.
   * @returns Observable emitting the number of rows affected.
   */
  putDocTypeById(doctype: DocTypes): Observable<number> {
    return this.http.put<number>(this._getApiUrl('/DocTypes/' + doctype.DOCTYPEID), doctype, {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Main-Currency': this.globalsettingsservice.getMainCurrencyId().toString(),
        }
      })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  // ---------------------------------------------------------------------------
  // Document line items (DocItems)
  // ---------------------------------------------------------------------------

  /**
   * Returns all line items for a given document.
   *
   * Calls `GET /DocItemsList/:id`.
   *
   * @param id - Document ID (`DOCID`).
   * @returns Observable emitting an array of `DocItems` records.
   */
  getDocItemsByDoc(id: number): Observable<DocItems[]> {
    return this.http.get<DocItems[]>(this._getApiUrl('/DocItemsList/' + id), {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Main-Currency': this.globalsettingsservice.getMainCurrencyId().toString(),
        }
      })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Deletes the entire document record including all its line items.
   *
   * Calls `DELETE /Document/:id`.
   *
   * @param id - Document ID (`DOCID`) to delete.
   * @returns Observable emitting the number of rows affected.
   */
  deleteDocumentById(id: number): Observable<number> {
    return this.http.delete<number>(this._getApiUrl('/Document/' + id), {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Main-Currency': this.globalsettingsservice.getMainCurrencyId().toString(),
        }
      })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Deletes all line items belonging to a document without deleting the
   * document header itself.
   *
   * Calls `DELETE /Document/:id/Items`.
   *
   * @param id - Document ID (`DOCID`) whose items should be cleared.
   * @returns Observable emitting the number of rows affected.
   */
  deleteAllDocItemByDocId(id: number): Observable<number> {
    return this.http.delete<number>(this._getApiUrl('/Document/' + id + '/Items'), {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Main-Currency': this.globalsettingsservice.getMainCurrencyId().toString(),
        }
      })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Deletes a single document line item by its primary key.
   *
   * Calls `DELETE /DocItems/:id`.
   *
   * @param id - Line item ID (`DOCITEMID`).
   * @returns Observable emitting the number of rows affected.
   */
  deleteDocItemById(id: number): Observable<number> {
    return this.http.delete<number>(this._getApiUrl('/DocItems/' + id), {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Main-Currency': this.globalsettingsservice.getMainCurrencyId().toString(),
        }
      })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Updates a single document line item.
   *
   * Calls `PUT /DocItems/:DOCITEMID`.
   *
   * @param di - The `DocItems` record to update. `DOCITEMID` must be set.
   * @returns Observable emitting the number of rows affected.
   */
  putDocItemById(di: DocItems): Observable<number> {
    return this.http.put<number>(this._getApiUrl('/DocItems/' + di.DOCITEMID), di, {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Main-Currency': this.globalsettingsservice.getMainCurrencyId().toString(),
        }
      })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Creates a new document line item.
   *
   * Calls `POST /DocItems/:DOCITEMID`.
   *
   * @param di - The `DocItems` record to create.
   * @returns Observable emitting the server response (typically the new ID).
   */
  postDocItemById(di: DocItems): Observable<any> {
    return this.http.post<any>(this._getApiUrl('/DocItems/' + di.DOCITEMID), di, {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Main-Currency': this.globalsettingsservice.getMainCurrencyId().toString(),
        }
      })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Returns a single document line item by its primary key.
   *
   * Calls `GET /DocItems/:id`.
   *
   * @param id - Line item ID (`DOCITEMID`).
   * @returns Observable emitting a one-element `DocItems` array.
   */
  getDocItemById(id: number): Observable<DocItems[]> {
    return this.http.get<DocItems[]>(this._getApiUrl('/DocItems/' + id), {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Main-Currency': this.globalsettingsservice.getMainCurrencyId().toString(),
        }
      })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Returns the total quantity received (purchased/inbounded) for a given line
   * item up to a reference date.
   *
   * Calls `GET /DocItems/:id/GotItems/?_fromDate=<fromDate>`.
   *
   * @param id - Line item ID (`DOCITEMID`).
   * @param fromDate - Reference date for the received quantity calculation.
   * @returns Observable emitting the received quantity as a number.
   */
  getDocItemsReceivedByDoc(id: number, fromDate: Date): Observable<number> {
    return this.http.get<number>(this._getApiUrl('/DocItems/' + id + '/GotItems/?_fromDate=' + (fromDate ? new Date(fromDate) : new Date()).toISOString()), {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Main-Currency': this.globalsettingsservice.getMainCurrencyId().toString(),
        }
      })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Returns the total quantity shipped for a given line item up to a reference
   * date.
   *
   * Calls `GET /DocItems/:id/Shipped/?_fromDate=<fromDate>`.
   *
   * @param id - Line item ID (`DOCITEMID`).
   * @param fromDate - Reference date for the shipped quantity calculation.
   * @returns Observable emitting `[{ DOCITEMSHIPPED: number }]`.
   */
  getDocItemsShippedByDoc(id: number, fromDate: Date): Observable<{ DOCITEMSHIPPED: number }[]> {
    return this.http.get<{ DOCITEMSHIPPED: number }[]>(this._getApiUrl('/DocItems/' + id + '/Shipped/?_fromDate=' + (fromDate ? new Date(fromDate) : new Date()).toISOString()), {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Main-Currency': this.globalsettingsservice.getMainCurrencyId().toString(),
        }
      })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  // ---------------------------------------------------------------------------
  // Document header CRUD
  // ---------------------------------------------------------------------------

  /**
   * Creates a new document record.
   *
   * Calls `POST /Document/:DOCID`.
   *
   * @param doc - The `Document` to create.
   * @returns Observable emitting the new document's `DOCID`.
   */
  postDocumentById(doc: Document): Observable<number> {
    return this.http.post<number>(this._getApiUrl('/Document/' + doc.DOCID), doc, {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Main-Currency': this.globalsettingsservice.getMainCurrencyId().toString(),
        }
      })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * `POST /Document/:DOCID` exactly once -- unlike {@link postDocumentById}, no
   * `retry(1)` and no `handleError` browser `alert`. Creation is not idempotent
   * (the server assigns `MAX(DOCID) + 1`), so a retry after an uncertain
   * response could silently create a second document. Errors propagate as-is
   * for the caller to interpret. Used by the bank-payment auto-create path.
   *
   * @param doc - The `Document` to create.
   * @returns Observable emitting `{ DOCID }` of the new document.
   */
  postDocumentByIdOnce(doc: Document): Observable<{ DOCID: number }> {
    return this.http.post<{ DOCID: number }>(this._getApiUrl('/Document/' + doc.DOCID), doc, {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Main-Currency': this.globalsettingsservice.getMainCurrencyId().toString(),
        }
      });
  }

  /**
   * `POST /DocItems/:DOCITEMID` exactly once -- same reasoning and contract as
   * {@link postDocumentByIdOnce}: no `retry(1)`, no `handleError` alert.
   *
   * @param di - The `DocItems` record to create.
   */
  postDocItemByIdOnce(di: DocItems): Observable<unknown> {
    return this.http.post<unknown>(this._getApiUrl('/DocItems/' + di.DOCITEMID), di, {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Main-Currency': this.globalsettingsservice.getMainCurrencyId().toString(),
        }
      });
  }

  /**
   * Updates an existing document record.
   *
   * Calls `PUT /Document/:DOCID`.
   *
   * @param doc - The `Document` to update. `DOCID` must be set.
   * @returns Observable emitting the number of rows affected.
   */
  putDocumentById(doc: Document): Observable<number> {
    return this.http.put<number>(this._getApiUrl('/Document/' + doc.DOCID), doc, {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Main-Currency': this.globalsettingsservice.getMainCurrencyId().toString(),
        }
      })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Returns a single document header record by ID.
   *
   * Calls `GET /Document/:id`.
   *
   * @param id - Document ID (`DOCID`).
   * @returns Observable emitting a one-element `Document` array.
   */
  getDocumentById(id: number): Observable<Document[]> {
    return this.http.get<Document[]>(this._getApiUrl('/Document/' + id), {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Main-Currency': this.globalsettingsservice.getMainCurrencyId().toString(),
        }
      })
      .pipe(
        retry(1),
        map(docs => docs.map(d => ({
          ...d,
          DOCDATE: new Date(d.DOCDATE),
          DOCEXPDATE: new Date(d.DOCEXPDATE),
          DOCVALDATE: new Date(d.DOCVALDATE),
        }))),
        catchError(this.handleError)
      )
  }

  /**
   * Returns the human-readable display name string for a document.
   *
   * Calls `GET /Document/:id/Name`.
   *
   * @param id - Document ID (`DOCID`).
   * @returns Observable emitting `[{ DOCNAMESTR: string }]`.
   */
  getDocumentNameById(id: number): Observable<{ DOCNAMESTR: string }[]> {
    return this.http.get<{ DOCNAMESTR: string }[]>(this._getApiUrl('/Document/' + id + '/Name'), {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Main-Currency': this.globalsettingsservice.getMainCurrencyId().toString(),
        }
      })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Returns the list of payment documents linked to a given document, with
   * amounts converted to the specified currency.
   *
   * Calls `GET /Document/:docid/PaymentsList/?currency=<curId>&topDate=<topDate>`.
   *
   * @param docid - Parent document ID (`DOCID`).
   * @param curId - Currency ID for amount conversion.
   * @param topDate - Upper date boundary for payment records.
   * @returns Observable emitting an array of payment summary records.
   */
  getDocumentPaymentsList(docid: number, curId: number, topDate: Date): Observable<any[]> {
    return this.http.get<any[]>(this._getApiUrl('/Document/' + docid + '/PaymentsList/' + '?currency=' + curId + '&topDate=' + (topDate ? new Date(topDate) : new Date()).toISOString() ), {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Main-Currency': this.globalsettingsservice.getMainCurrencyId().toString(),
        }
      })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Returns the manufacturing/production status for items in a document,
   * checking stock receipts across up to four warehouse/stock types.
   *
   * Calls `GET /Document/:docid/Manufactured/:st1/:st2/:st3/:st4?topDate=<topDate>`.
   *
   * @param docid - Document ID (`DOCID`).
   * @param st1 - First stock type ID.
   * @param st2 - Second stock type ID.
   * @param st3 - Third stock type ID.
   * @param st4 - Fourth stock type ID.
   * @param topDate - Upper date boundary for the manufacturing status check.
   * @returns Observable emitting an array of manufacturing status records.
   */
  getDocumentManufacturedStatus(docid: number, st1: number, st2: number, st3: number, st4: number, topDate: Date): Observable<any[]> {
    return this.http.get<any[]>(this._getApiUrl('/Document/' + docid + '/Manufactured/' + st1 + '/' + st2 + '/' + st3 + '/' + st4 + '?topDate=' + (topDate ? new Date(topDate) : new Date()).toISOString()), {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Main-Currency': this.globalsettingsservice.getMainCurrencyId().toString(),
        }
      })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Recomputes a manufacturing backward-leg document's price from its actual
   * matching forward legs, as of today — the DB-side "what SHOULD this price
   * be" check behind the manufacturing backdoc price-check dialog. See
   * `docs/PROMPT_manufacturing-backdoc-price-check-dialog.md` and
   * `docs/PROMPT_manufacturing-facility-mechanism.md`.
   *
   * Calls `GET /Document/:backDocId/ManufacturingRecompute?_timeWindowMinutes=<minutes>`.
   *
   * @param backDocId - Backward-leg document ID (`DOCID`, DOCTYPE=5).
   * @param timeWindowMinutes - Matching time window in minutes (defaults to 15,
   *   matching `dbo.ufn_getmanufacturingbackdoc_recompute_iTVF`'s own default).
   * @returns Observable emitting one row per `DocItems` line on the backward
   *   document (normally exactly one).
   */
  getManufacturingBackdocRecompute(backDocId: number, timeWindowMinutes: number = 15): Observable<ManufacturingBackdocRecompute[]> {
    return this.http.get<ManufacturingBackdocRecompute[]>(this._getApiUrl('/Document/' + backDocId + '/ManufacturingRecompute?_timeWindowMinutes=' + timeWindowMinutes), {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Main-Currency': this.globalsettingsservice.getMainCurrencyId().toString(),
        }
      })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Per-forward-leg breakdown behind {@link getManufacturingBackdocRecompute}'s
   * `RecomputedUnitPriceCurrency`/`RecomputedUnitPriceMain` — the "show your
   * work" companion, same relationship as `getProdIncomePriceBreakdownById`
   * is to `getProdIncomePriceById`.
   *
   * Calls `GET /Document/:backDocId/ManufacturingRecomputeBreakdown?_timeWindowMinutes=<minutes>`.
   *
   * @param backDocId - Backward-leg document ID (`DOCID`, DOCTYPE=5).
   * @param timeWindowMinutes - Same meaning as {@link getManufacturingBackdocRecompute} (defaults to 15).
   * @returns Observable emitting one row per matched forward leg. Empty array
   *   is a normal "no matching forward legs found" result, not an error.
   */
  getManufacturingBackdocRecomputeBreakdown(backDocId: number, timeWindowMinutes: number = 15): Observable<ManufacturingBackdocRecomputeBreakdownRow[]> {
    return this.http.get<ManufacturingBackdocRecomputeBreakdownRow[]>(this._getApiUrl('/Document/' + backDocId + '/ManufacturingRecomputeBreakdown?_timeWindowMinutes=' + timeWindowMinutes), {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Main-Currency': this.globalsettingsservice.getMainCurrencyId().toString(),
        }
      })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Returns the topmost invoice/sales-order ancestor document in the document
   * tree for a given document.
   *
   * Calls `GET /Document/:id/TopDoc`.
   *
   * @param id - Any document ID (`DOCID`) in the tree.
   * @returns Observable emitting the root `Document` record.
   */
  getTopmostInvoiceDocumentById(id: number): Observable<Document[]> {
    return this.http.get<Document[]>(this._getApiUrl('/Document/' + id + '/TopDoc'), {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Main-Currency': this.globalsettingsservice.getMainCurrencyId().toString(),
        }
      })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Performs a fast (indexed/typeahead) document search by document number only —
   * backs `DocumentselectorComponent`'s live type-ahead.
   *
   * Calls `GET /DocumentList/fastSearchByNumber?_document=...`. Backed by the
   * NEW `[dbo].[WY_tvf_searchDocumentByNumber_iTVF]` (`SQL-Database-migration`
   * PR #79), not `[dbo].[WY_tvf_searchDocumentFast]` (the old multi-field
   * function this method originally called) — real live evidence (2026-09-16,
   * WORKLOG.md) confirmed that one is genuinely slow/unreliable under
   * keystroke-driven traffic: up to 13.4s on success, and outright 30s
   * `mssql` request timeouts on two adjacent calls. Root cause (the owner
   * supplied the function body): an unconditional 8-way JOIN fan-out
   * regardless of which of its 4 search fields was populated (this method
   * only ever sent `_document`, never seller/buyer/product), a scalar UDF
   * called twice per row, and leading-wildcard `LIKE` on every field. The new
   * function/route is document-number-only by design — this method's
   * `sellerStr`/`buyerStr`/`productStr` parameters are gone, not just unused.
   *
   * @param docStr - Document number search string (maps to `_document`;
   *   prefix-matched server-side, not substring).
   * @param cliid - Optional `DOCCLIENTSID` filter (maps to `_cliid`). `null`/unset
   *   means no filter, same scope as before this parameter existed (PR #80).
   * @param lcocliid - Optional `DOCLCOCLIID` filter (maps to `_lcocliid`) — the
   *   local/legal entity issuing the document. `null`/unset means no filter.
   * @param doctypeFilter - Optional `DOCTYPE` filter (maps to `_doctype`), e.g.
   *   `1` to restrict to Invoices. `null`/unset means no filter.
   * @returns Observable emitting matching {@link DocumentFastSearchResult} rows.
   *
   * Deliberately skips both the shared `handleError` (blocking `alert()`) and the
   * `retry(1)` convention every other method here uses (same precedent
   * `deletePriceVariableById` already set for skipping `handleError` when it's
   * the wrong UX for the call) — this fires automatically per keystroke via
   * `DocumentselectorComponent`'s debounced pipeline, not from a user-initiated
   * action: an `alert()` per failed background search would block the whole UI
   * thread while the user is still typing, and an automatic retry would only
   * add more load to a query that already timed out once. The caller's own
   * `catchError` already logs and degrades gracefully (keeps the previous
   * suggestion list rather than crashing the pipeline).
   */
  getDocumentFastSearch(docStr: string, cliid: number = null, lcocliid: number = null, doctypeFilter: number = null): Observable<DocumentFastSearchResult[]> {
    let queryString = '?_document=' + encodeURIComponent(docStr);
    if (cliid != null) { queryString += '&_cliid=' + cliid; }
    if (lcocliid != null) { queryString += '&_lcocliid=' + lcocliid; }
    if (doctypeFilter != null) { queryString += '&_doctype=' + doctypeFilter; }

    return this.http.get<DocumentFastSearchResult[]>(this._getApiUrl('/DocumentList/fastSearchByNumber' + queryString), {
      headers: {
        ...this.globalsettingsservice.getStandardApiHeaders(),
        'Webyto-Main-Currency': this.globalsettingsservice.getMainCurrencyId().toString(),
      }
    });
  }

  /**
   * Searches for documents matching the given filter criteria.
   *
   * Calls `GET /Document/FindByParameters?_filter=<encoded>`.
   *
   * Supported filter modes:
   * - `PRODID` — match documents containing a specific product
   * - `DOCTYPE` — match documents of specified type IDs (array)
   * - `DOCFIELD` — match by a field/value pair with an optional operator
   *
   * @param filter - Filter object serialised and URL-encoded as `_filter`.
   * @returns Observable emitting matching `Document` records.
   */
  findDocumentsByParameters(filter: any): Observable<Document[]> {
    let parmStr: string = '';
    if (filter) {
      parmStr = '?_filter=' + encodeURIComponent(JSON.stringify(filter));
    }
    return this.http.get<Document[]>(this._getApiUrl('/Document/FindByParameters' + parmStr), {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Main-Currency': this.globalsettingsservice.getMainCurrencyId().toString(),
        }
      })
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  // ---------------------------------------------------------------------------
  // Batch enrichment endpoints
  // ---------------------------------------------------------------------------

  /**
   * Batch replacement for the per-row `getProdInfoById` +
   * `getDocItemsShippedByDoc` pattern.
   *
   * Calls `GET /DocItemsList/:docId/StockAndShipped`. Returns stock level and
   * shipped quantity for every line item of the document in a single DB
   * round-trip, replacing N individual requests.
   *
   * @param docId - Document ID (`DOCID`).
   * @param cmId - Company ID used for stock resolution (`_cm`).
   * @param s1 - Primary depot ID (0 to exclude).
   * @param s2 - Secondary depot ID (0 to exclude).
   * @param s3 - Tertiary depot ID (0 to exclude).
   * @param fromDate - Reference date for stock and shipped quantity calculation.
   * @returns Observable emitting `DocItemStockSummary[]`.
   */
  getDocItemsStockAndShipped(
    docId: number,
    cmId: number,
    s1: number,
    s2: number = 0,
    s3: number = 0,
    fromDate: Date = new Date()
  ): Observable<DocItemStockSummary[]> {
    const params = `?_cm=${cmId}&_s1=${s1}&_s2=${s2}&_s3=${s3}&_fromDate=${(fromDate ? new Date(fromDate) : new Date()).toISOString()}`;
    return this.http.get<DocItemStockSummary[]>(
      this._getApiUrl('/DocItemsList/' + docId + '/StockAndShipped' + params),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Main-Currency': this.globalsettingsservice.getMainCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Returns all line items for a document enriched with price, stock, income
   * price, shipped quantity, product metadata, and ACTC status — all in a
   * single DB round-trip.
   *
   * Calls `GET /DocItemsList/:docId/EnrichedItems`. Replaces the N×8 per-row
   * HTTP call pattern used by the old
   * `pullDiRowPriceAndStockObservable`/`pullDiRowStockIncomePriceObservable`
   * methods.
   *
   * @param docId - Document ID (`DOCID`).
   * @param cmId - Company ID.
   * @param crId - Currency ID for price conversion.
   * @param s1 - Main depot ID.
   * @param s2 - Import depot ID (0 to exclude).
   * @param s3 - Auxiliary depot ID (0 to exclude).
   * @param fromDate - Reference date.
   * @returns Observable emitting `EnrichedDocItem[]`.
   */
  getDocItemsEnriched(
    docId: number,
    cmId: number,
    crId: number,
    s1: number,
    s2: number = 0,
    s3: number = 0,
    fromDate: Date = new Date()
  ): Observable<EnrichedDocItem[]> {
    const params = `?_cm=${cmId}&_cr=${crId}&_s1=${s1}&_s2=${s2}&_s3=${s3}&_fromDate=${(fromDate ? new Date(fromDate) : new Date()).toISOString()}`;
    return this.http.get<EnrichedDocItem[]>(
      this._getApiUrl('/DocItemsList/' + docId + '/EnrichedItems' + params),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Main-Currency': this.globalsettingsservice.getMainCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  // ---------------------------------------------------------------------------
  // Error handling
  // ---------------------------------------------------------------------------

  /**
   * Centralised HTTP error handler used by all methods in this service.
   *
   * Extracts a human-readable message, shows a browser `alert` (legacy pattern
   * — Phase 1 will replace with a toast), and re-throws so the calling
   * Observable terminates.
   *
   * @param error - Angular `HttpErrorResponse` or `ErrorEvent`.
   * @returns An Observable that immediately errors with the formatted message.
   */
  handleError(error) {
    let errorMessage = '';
    if (error.error instanceof ErrorEvent) {
      // Get client-side error
      errorMessage = error.error.message;
    } else {
      // Get server-side error
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    window.alert(errorMessage);
    return throwError(() => errorMessage);
  }
}
